import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Repeat, Check, X, SkipForward, ShieldCheck, Reply, Inbox } from 'lucide-react';
import api from '../services/api';

// AI follow-up sequence status for one lead (server/services/followUpService.js).
// Renders nothing when the lead was never enrolled.

const STOP_REASONS = {
  customer_replied: 'The customer replied',
  customer_opted_out: 'They texted STOP. Texts to this number are blocked',
  customer_called: 'The customer called',
  call_connected: 'Your team spoke to them on the phone',
  stage_changed: 'The lead moved to a stage without follow-ups',
  do_not_call: 'The lead is on Do Not Call',
  sms_opt_out: 'They texted STOP and have no email to reach them on',
  email_unsubscribed: 'They unsubscribed from email',
  no_contact_channel: 'No phone or email to reach them',
  lead_archived: 'The lead was archived',
  feature_disabled: 'Follow-ups were turned off in settings',
  stopped_by_user: 'Stopped by your team',
  no_send_slot: 'No allowed sending time (check business hours and send window)',
};

const CHANNEL_LABELS = { sms: 'SMS', email: 'email', messenger: 'Messenger', instagram: 'Instagram', whatsapp: 'WhatsApp' };

const STATUS_STYLE = {
  active: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed: 'bg-green-50 text-green-700 border-green-200',
  paused: 'bg-amber-50 text-amber-800 border-amber-200',
  stopped: 'bg-gray-100 text-gray-600 border-gray-200',
};

// Times are shown in the ORGANIZATION's timezone (with its abbreviation),
// not the viewer's: the schedule is planned in the org's local time, and
// everyone on the team should see the same clock.
const makeFmt = (timeZone) => (d) => {
  const opts = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
  try {
    return new Date(d).toLocaleString('en-US', { ...opts, timeZone: timeZone || 'UTC' });
  } catch (_) {
    return new Date(d).toLocaleString('en-US', opts); // unknown tz string
  }
};

const OUTCOME = {
  sent: { Icon: Check, cls: 'text-green-600', label: 'Sent' },
  fallback: { Icon: ShieldCheck, cls: 'text-violet-600', label: 'Sent (safe check-in)' },
  skipped: { Icon: SkipForward, cls: 'text-gray-400', label: 'Skipped' },
};

const LeadFollowUpWidget = ({ leadId, stageId }) => {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  // stageId in the key: moving the lead pauses / resumes the sequence, so
  // the card refetches as soon as the stage changes.
  const { data } = useQuery(
    ['lead-follow-up', leadId, stageId ? String(stageId) : null],
    () => api.get(`/follow-ups/lead/${leadId}`).then((r) => r.data),
    { enabled: !!leadId, staleTime: 30 * 1000 }
  );

  const stop = useMutation(
    () => api.post(`/follow-ups/lead/${leadId}/stop`).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Follow-ups stopped for this lead');
        setConfirming(false);
        queryClient.invalidateQueries(['lead-follow-up', leadId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not stop follow-ups'),
    }
  );

  const enr = data?.enrollment;
  if (!enr) return null;
  const fmt = makeFmt(enr.timezone);

  const live = enr.status === 'active' || enr.status === 'paused';
  // Switched off in Settings: nothing is sent, so don't promise a next
  // check-in (switching off ends running sequences; this covers any left).
  const off = live && enr.featureEnabled === false;
  const statusLabel = off ? 'Turned off'
    : enr.status === 'active' ? `Step ${enr.step} of ${enr.total}`
      : enr.status === 'paused' ? 'Paused'
        : enr.status === 'completed' ? 'Completed' : 'Stopped';

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Repeat size={20} className="mr-2" />
          AI follow-ups
        </h3>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${off ? STATUS_STYLE.stopped : STATUS_STYLE[enr.status] || STATUS_STYLE.stopped}`}>
          {statusLabel}
        </span>
      </div>
      <div className="card-body space-y-3 text-sm">
        {off && (
          <div className="text-gray-700">
            Follow-ups are turned off in Settings. Nothing is sent while they are off.
          </div>
        )}
        {!off && enr.status === 'active' && enr.nextAt && (
          <div className="text-gray-700">
            Next check-in: <span className="font-medium">{fmt(enr.nextAt)}</span>
            <div className="text-xs text-gray-500 mt-0.5">Stops on its own if the customer replies or your team reaches them by phone.</div>
          </div>
        )}
        {!off && enr.status === 'paused' && (
          <div className="text-gray-700">
            Paused while the lead is in a stage without follow-ups.
            <div className="text-xs text-gray-500 mt-0.5">
              {enr.activeStageNames?.length
                ? `Resumes from step ${enr.step} if the lead moves back to ${enr.activeStageNames.join(' or ')}.`
                : `Resumes from step ${enr.step} if the lead moves back to a follow-up stage.`}
            </div>
          </div>
        )}
        {enr.status === 'stopped' && (
          <div className="text-gray-700">
            {enr.stopReason === 'stopped_by_user' && enr.stoppedByName
              ? `Stopped by ${enr.stoppedByName}`
              : (STOP_REASONS[enr.stopReason] || enr.stopReason)}
            {enr.stoppedAt ? ` · ${fmt(enr.stoppedAt)}` : ''}
          </div>
        )}
        {enr.reply && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-medium text-green-800">
              <span className="inline-flex items-center gap-1">
                <Reply size={13} />
                {`Replied${enr.reply.day != null ? ` to Day ${enr.reply.day}` : ''}${enr.reply.channel ? ` by ${CHANNEL_LABELS[enr.reply.channel] || enr.reply.channel}` : ''} · ${fmt(enr.reply.at)}`}
              </span>
              {!enr.reply.answered && (
                <span className="rounded-full border border-green-300 bg-white px-2 py-0.5">Waiting for your reply</span>
              )}
            </div>
            {enr.reply.preview && (
              <p className="mt-1.5 text-sm text-gray-900 whitespace-pre-wrap break-words">{enr.reply.preview}</p>
            )}
            <Link
              to={`/inbox?leadId=${leadId}`}
              className="mt-2 inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
            >
              <Inbox size={13} /> Reply in Inbox
            </Link>
          </div>
        )}

        <ol className="space-y-1.5">
          {enr.days.map((day, i) => {
            const h = (enr.history || []).find((x) => x.step === i + 1);
            const o = h && OUTCOME[h.outcome];
            const upcoming = !h && live && !off && i + 1 === enr.step;
            return (
              <li key={day} className="flex items-center gap-2 text-xs">
                <span className="w-12 flex-shrink-0 text-gray-500">Day {day}</span>
                {o ? (
                  <span className={`inline-flex items-center gap-1 ${o.cls}`}>
                    <o.Icon size={13} /> {o.label}{h.channel ? ` · ${h.channel.toUpperCase()}` : ''} · {fmt(h.at)}
                  </span>
                ) : (
                  <span className={upcoming ? 'text-indigo-600 font-medium' : 'text-gray-400'}>
                    {upcoming ? (enr.status === 'paused' ? 'Next when resumed' : 'Up next') : off ? 'On hold' : live ? 'Scheduled' : 'Not sent'}
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        {live && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {confirming ? (
              <>
                <span className="text-xs text-gray-600">Stop all remaining follow-ups?</span>
                <button
                  type="button"
                  onClick={() => stop.mutate()}
                  disabled={stop.isLoading}
                  className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-50"
                >
                  {stop.isLoading ? 'Stopping…' : 'Yes, stop'}
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
              >
                <X size={13} /> Stop follow-ups
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadFollowUpWidget;
